import { FC, useEffect, useState } from 'react'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerText from '../../components/custom_components/Text'
import { useTranslation } from 'react-i18next'
import { Badge, Card, Spin, Table } from 'antd'
import PerSpacer from '../../components/custom_components/Spacer'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'
import { useActivityListInitialData } from '../../graphql/activity-list/hooks/useActivityListInitialData'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { ActivityListDataDto, ActivityListDto, ActivityListType } from '../../types'
import { useActivityListData } from '../../graphql/activity-list/hooks/useActivityListData'
import { showNotification } from '../../components/custom_components/Notification/notification'
import PerButton from '../../components/custom_components/Button'
import { useDateTime } from '../../shared/useDateTime'
import { useAuth } from '../../context/auth-context'

interface EmployeeActivityListComponentProps {
  employeeId: string
  title: string
  lastYear?: boolean
}

export const EmployeeActivityListComponent: FC<EmployeeActivityListComponentProps> = ({ employeeId, title, lastYear = false }) => {
  const { t } = useTranslation()

  const { data, error, loading } = useActivityListInitialData(employeeId, lastYear)

  const [tableData, setTableData] = useState<ActivityListDto[]>([])
  const [isRetrieving, toggleRetrieving] = useState<boolean>(false)

  const [getTypeData] = useActivityListData()

  useEffect(() => {
    if (!loading && !error && !!data && !!data.retrieveActivityListInitialData) {
      setTableData(data.retrieveActivityListInitialData)
    }
  }, [data, loading, error])

  const retrieveActivityListData = async (type: ActivityListType, page: number, length: number): Promise<boolean> => {
    const { data, error } = await getTypeData({
      variables: {
        employeeId,
        type,
        page,
        length,
        lastYear,
      },
    })
    if (!!error || !data || !data.retrieveActivityListData || !data.retrieveActivityListData.length) {
      return false
    }
    setTableData((prev) => prev.map((el) => (el.type === type ? { ...el, data: [...el.data, ...data.retrieveActivityListData!] } : el)))
    return true
  }

  const queryLoadingOrError = !data || !data.retrieveActivityListInitialData || error || loading

  const accordionMarginBottom = 10

  const activityListTypes = Object.values(ActivityListType)
  const numSections = activityListTypes.length

  const cardHeight = 50

  const backgroundCardHeight = numSections * cardHeight + (numSections - 1) * accordionMarginBottom

  return (
    <>
      <PerText appearance="tile-heading" mb="s">
        {title}
      </PerText>

      <Card bordered={false}>
        {queryLoadingOrError && (
          <NewLoadingAndErrors
            backgroundCardHeight={backgroundCardHeight}
            isLoading={loading}
            error={error}
            hasNoData={!data || !data.retrieveActivityListInitialData}
            showSpinner
            t={t}
          />
        )}
        {!queryLoadingOrError &&
          activityListTypes.map((type: ActivityListType, index: number) => {
            const tableDataElement: ActivityListDto | undefined = tableData.find((el) => el.type === type)
            const dataLength: number | undefined = !!tableDataElement ? tableDataElement.length : undefined
            const retrievedData: ActivityListDataDto[] = !!tableDataElement ? tableDataElement.data : []
            const isLastElement = index !== activityListTypes.length - 1
            return (
              <ActivityListSection
                key={type}
                height={cardHeight}
                marginBottom={isLastElement ? accordionMarginBottom : undefined}
                type={type}
                length={dataLength !== undefined ? dataLength : undefined}
                data={retrievedData}
                isRetrieving={isRetrieving}
                onViewMore={async (type: ActivityListType, page: number, length: number): Promise<boolean> => {
                  toggleRetrieving(true)
                  const ok = await retrieveActivityListData(type, page, length)
                  if (!ok) {
                    showNotification({
                      type: 'error',
                      message: t('profile.activityList.notifications.error.title'),
                      description: t('profile.activityList.notifications.error.description'),
                    })
                    toggleRetrieving(false)
                    return false
                  }
                  toggleRetrieving(false)
                  return true
                }}
              />
            )
          })}
      </Card>
    </>
  )
}

const ActivityListSection: FC<{
  type: ActivityListType
  height: number
  marginBottom: number | undefined
  length: number | undefined
  data: ActivityListDataDto[]
  isRetrieving: boolean
  onViewMore: (type: ActivityListType, page: number, length: number) => Promise<boolean>
}> = ({ type, height, marginBottom, length, data, isRetrieving, onViewMore }) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [moreButtonDisabled, toggleMoreButtonDisabled] = useState<boolean>(false)
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)

  const activityListColumns = [
    {
      key: 'type',
      title: t('profile.activityList.table.header.type'),
      className: 'textAlign-left',
      width: '30%',
      render: (data: ActivityListDataDto) => <PerText>{data.type || '-'}</PerText>,
    },
    {
      key: 'name',
      title: t('profile.activityList.table.header.name'),
      className: 'textAlign-left',
      width: '40%',
      render: (data: ActivityListDataDto) => <PerText>{data.name || '-'}</PerText>,
    },
    {
      key: 'date',
      title: t('profile.activityList.table.header.date'),
      className: 'textAlign-left',
      width: '20%',
      render: (data: ActivityListDataDto) => <PerText>{formatDateShort(data.date)}</PerText>,
    },
    {
      key: 'url',
      title: t('profile.activityList.table.header.link'),
      className: 'textAlign-center',
      width: '10%',
      render: (data: ActivityListDataDto) =>
        !!data.url ? <PerButtonCircle asLink={data.url} variant="pastel" icon="link" color="primary" size="small" /> : <PerText>-</PerText>,
    },
  ]

  const loadMoreData = async (type: ActivityListType, length: number) => {
    const ok = await onViewMore(type, currentPage, length)
    if (ok) {
      setCurrentPage(currentPage + 1)
    } else if (!data.length) {
      setExpanded(!expanded)
    }
  }

  return (
    <>
      <Card
        size="small"
        style={{
          height: `${height}px`,
          background: 'white',
          display: 'inline-block',
          marginBottom: `${marginBottom !== undefined ? `${marginBottom}px` : undefined}`,
        }}
      >
        <PerFlexRow align="middle" justify="space-between" className="fullHeight">
          <PerFlexRow direction="column" ml="xxs" align="middle">
            <PerFlexRow align="middle">
              <PerText appearance="tile-heading" mr="xs">
                {type.split('_').join(' ')}
              </PerText>
              <Badge
                count={length !== undefined ? length : '?'}
                showZero
                title={length === undefined ? t('profile.activityList.errorTooltip') : ''}
                style={{
                  backgroundColor: '#ff6259',
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '12px',
                  height: '22px',
                  lineHeight: '22px',
                  padding: '0px 8px',
                }}
              ></Badge>
            </PerFlexRow>
          </PerFlexRow>
          {!!length && (
            <PerFlexRow direction="column" mr="xxs" align="middle">
              <PerButtonCircle
                icon={expanded ? 'chevronUp_stroke' : 'chevronDown_stroke'}
                color="secondary"
                variant="pastel"
                onClick={async () => {
                  const expandedValue = expanded
                  setExpanded(!expanded)
                  if (!expandedValue && !data.length) {
                    toggleMoreButtonDisabled(true)
                    await loadMoreData(type, length)
                    toggleMoreButtonDisabled(false)
                  }
                }}
              />
            </PerFlexRow>
          )}
        </PerFlexRow>
      </Card>
      {expanded && (
        <PerSpacer ml="s" mr="s">
          {!data.length && (
            <NewLoadingAndErrors backgroundCardHeight={200} isLoading={moreButtonDisabled} hasNoData={false} showSpinner t={t} />
          )}
          {!!data.length && (
            <>
              <Table
                bordered={false}
                className="small"
                dataSource={data}
                rowKey={(record, index) => `${type}-${record.url ?? index}`}
                columns={activityListColumns}
                pagination={false}
                loading={isRetrieving && !data.length}
              />
              {!!length && data.length < length && (
                <PerFlexRow align="middle" justify="start" className="fullHeight">
                  <PerButton
                    textColor="#05CBCE"
                    underlineText
                    color="transparent"
                    variant="pastel"
                    text={t('global.viewMore')}
                    disabled={moreButtonDisabled}
                    onClick={async () => {
                      toggleMoreButtonDisabled(true)
                      await loadMoreData(type, length)
                      toggleMoreButtonDisabled(false)
                    }}
                  />
                  {moreButtonDisabled && <Spin></Spin>}
                </PerFlexRow>
              )}
            </>
          )}
        </PerSpacer>
      )}
    </>
  )
}
